import { useTranslation } from '@thoughtspot/i18n';
import { Checkbox } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styles from './checklist-section.module.scss';
const I18N = {
    SELECT_ALL: 'smartCheckboxCollection.SELECT_ALL_ITEMS',
    CLEAR: 'Clear',
    SHOW_SELECTED: 'autoWorksheet.selectedItemsToggle.SHOW_SELECTED',
};
const DEBOUNCE_MS = 300;
export const ChecklistSection = ({ title, countLabel, items, loading, selectedIds, onSelectionChange, searchValue, onSearchChange, isServerSearch = false, optional = true, error, testIdPrefix, onBeforeToggle, }) => {
    const { t } = useTranslation();
    const [localSearch, setLocalSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const debounceRef = useRef();
    const [showSelected, setShowSelected] = useState(false);
    useEffect(() => {
        if (isServerSearch)
            return undefined;
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(localSearch);
        }, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [localSearch, isServerSearch]);
    const handleSearchInput = useCallback((_e, val) => {
        if (isServerSearch) {
            onSearchChange(val);
        }
        else {
            setLocalSearch(val);
        }
    }, [isServerSearch, onSearchChange]);
    const handleToggle = useCallback((item) => {
        if (item.disabled)
            return;
        const willCheck = !selectedIds.has(item.id);
        if (onBeforeToggle?.(item, willCheck) === false)
            return;
        onSelectionChange(prev => {
            const next = new Set(prev);
            if (next.has(item.id))
                next.delete(item.id);
            else
                next.add(item.id);
            return next;
        });
    }, [onSelectionChange, onBeforeToggle, selectedIds]);
    const filteredItems = useMemo(() => {
        if (isServerSearch || !debouncedSearch.trim())
            return items;
        const lower = debouncedSearch.toLowerCase();
        return items.filter(item => item.label.toLowerCase().includes(lower));
    }, [items, debouncedSearch, isServerSearch]);
    const displayItems = useMemo(() => showSelected
        ? filteredItems.filter(item => selectedIds.has(item.id))
        : filteredItems, [filteredItems, showSelected, selectedIds]);
    const selectedCount = selectedIds.size;
    useEffect(() => {
        if (selectedCount === 0)
            setShowSelected(false);
    }, [selectedCount]);
    const selectAll = useCallback(() => {
        onSelectionChange(prev => {
            const next = new Set(prev);
            filteredItems.forEach(item => {
                if (!item.disabled)
                    next.add(item.id);
            });
            return next;
        });
    }, [filteredItems, onSelectionChange]);
    const clearAll = useCallback(() => {
        onSelectionChange(prev => {
            const next = new Set(prev);
            filteredItems.forEach(item => next.delete(item.id));
            return next;
        });
    }, [filteredItems, onSelectionChange]);
    return (React.createElement(Vertical, null,
        title && (React.createElement(Typography, { variant: "content-label", className: styles.checklistSectionTitle },
            title,
            optional && (React.createElement(Typography, { variant: "p-small", overrideTag: "span", color: Colors.grayLight }, ` ${t('Optional')}`)))),
        React.createElement(Vertical, { spacing: "b" },
            React.createElement(Input.Text, { value: isServerSearch ? searchValue : localSearch, onChange: handleSearchInput, placeholder: t('cirrus.metadata.search.placeHolder'), prefixIcon: IconID.MAGNIFYING_GLASS, ariaLabel: t('cirrus.metadata.search.placeHolder'), inputClassName: styles.checklistSearchInput, "data-testid": testIdPrefix
                    ? `${testIdPrefix}-search`
                    : `checklist-search-${title}` }),
            React.createElement(Horizontal, { className: styles.checklistHeader },
                React.createElement(Typography, { variant: "caption", color: error ? Colors.failure : undefined }, error || `${countLabel} (${selectedCount})`),
                React.createElement(Horizontal, { spacing: "b" },
                    React.createElement(Typography, { variant: "caption", color: Colors.accent, className: styles.checklistActionLink, onClick: selectAll }, t(I18N.SELECT_ALL)),
                    React.createElement(Divider, { vertical: true, className: styles.checklistActionDivider }),
                    React.createElement(Typography, { variant: "caption", color: Colors.accent, className: styles.checklistActionLink, onClick: clearAll }, t(I18N.CLEAR)))),
            React.createElement(Vertical, { className: styles.checklistCard },
                React.createElement(Vertical, { grow: true, className: styles.checklistCheckboxList }, displayItems.length === 0 && !loading ? (React.createElement(Typography, { variant: "p-small", color: Colors.grayLight, className: styles.checklistEmptyState }, t('eurekaResultList.noResultsFound.title'))) : (displayItems.map(item => (React.createElement(Checkbox, { id: testIdPrefix
                        ? `${testIdPrefix}-${item.id}`
                        : item.id, checkboxControlClassName: styles.checkboxControl, key: item.id, label: item.label, checked: selectedIds.has(item.id), disabled: item.disabled, isList: true, onChange: () => handleToggle(item), showTooltipOnEllipsis: true, tooltipContent: item.label, className: styles.listCheckbox, "data-testid": testIdPrefix
                        ? `${testIdPrefix}-checkbox-${item.id}`
                        : undefined }))))),
                React.createElement(Vertical, { className: styles.checklistShowSelectedToggle },
                    React.createElement(Toggle, { checked: showSelected, disabled: selectedCount === 0, onChange: (e) => setShowSelected(e.target.checked), labelPosition: "right", label: t(I18N.SHOW_SELECTED), "data-testid": testIdPrefix
                            ? `show-selected-${testIdPrefix}-toggle`
                            : `showSelected-${title}` }))))));
};
//# sourceMappingURL=checklist-section.js.map